import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Session, Project, User } from '@claude-code-api/shared';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Skeleton } from '@/components/ui/skeleton';
import { QuickStats } from '@/components/dashboard/QuickStats';

const RUNNER_LABEL_KEYS: Record<string, string> = {
  'claude-code': 'claudeCode',
  'gift-ui': 'giftUi',
  acpx: 'acpx',
  'acpx-experimental': 'acpxExperimental',
  'acpx-team': 'acpxTeam',
};

function formatElapsedTime(createdAt: Date): string {
  const now = new Date();
  const diff = now.getTime() - new Date(createdAt).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function DashboardSidebarSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-24 rounded-lg" />
      <Skeleton className="h-24 rounded-lg" />
      <Skeleton className="h-24 rounded-lg" />
    </div>
  );
}

interface DashboardSidebarProps {
  sessions: Session[];
  projects: Project[];
  user: User | null;
  loading: boolean;
}

export function DashboardSidebar({
  sessions,
  projects,
  user,
  loading,
}: DashboardSidebarProps) {
  const { t } = useTranslation('dashboard');

  if (loading) return <DashboardSidebarSkeleton />;

  const activeSessions = sessions.filter((s) => s.status === 'running');

  return (
    <div className="space-y-6">
      <QuickStats
        projectCount={projects.length}
        activeSessions={activeSessions.length}
        maxSessions={user?.maxSessions || 1}
        maxCpu={user?.maxCpuCores || 2}
        maxRam={user?.maxMemoryGb || 4}
      />

      {user && (
        <div className="bg-surface-0 border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {t('credits')}
          </h3>
          <p
            className={`text-2xl font-bold ${!user.unlimitedCredits && user.creditBalance <= 0 ? 'text-destructive' : ''}`}
          >
            {user.unlimitedCredits
              ? 'Unlimited AI'
              : user.creditBalance.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('used', {
              count: Number(user.totalCreditsUsed).toLocaleString(),
            })}
          </p>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          {t('activeSessions')}
        </h3>
        {activeSessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('noActiveSessions')}
          </p>
        ) : (
          <div className="space-y-2">
            {activeSessions.map((session) => {
              const project = projects.find((p) => p.id === session.projectId);
              const projectName = project?.name || t('unknownProject');
              const runnerKey = RUNNER_LABEL_KEYS[session.runnerType];
              const runnerLabel = runnerKey
                ? t(`startSessionModal.runners.${runnerKey}.label`)
                : session.runnerType;

              return (
                <Link
                  key={session.id}
                  to={`/sessions/${session.id}`}
                  className="bg-surface-1 rounded-lg border p-3 flex items-center gap-3 hover:bg-surface-2 transition-colors block"
                >
                  <StatusIndicator status="running" size="sm" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {projectName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {runnerLabel} &middot;{' '}
                      {formatElapsedTime(session.createdAt)}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
